import { color } from "@tokens/index";
import type { TaskStatus } from "../taskStatus";

/**
 * Pure formatters for the workflow board. No React, no DOM.
 */

export function formatRelativeTime(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSeconds = Math.max(0, Math.floor((now - then) / 1000));
  if (diffSeconds < 60) return "just now";
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${String(diffMinutes)}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${String(diffHours)}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${String(diffDays)}d ago`;
  return new Date(iso).toLocaleDateString();
}

/** "send-rate-confirmation" -> "Send Rate Confirmation". */
export function prettifyTaskType(taskType: string): string {
  return taskType
    .split("-")
    .filter((p) => p.length > 0)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

export function statusDotColor(status: TaskStatus): string {
  switch (status) {
    case "open":
      return color.accent.primary;
    case "pending":
      return color.fg.muted;
    case "pending-approval":
      return color.accent.warning;
    case "completed":
      return color.accent.success;
  }
}

/**
 * Due date for a rail caption. Inside the coming week the weekday name is
 * shown; beyond that the month and day. Time of day is always included.
 */
export function formatShortDueDate(iso: string, now: Date): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const days = (date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
  if (days < 7) {
    return date.toLocaleString(undefined, {
      weekday: "short",
      hour: "numeric",
      minute: "2-digit",
    });
  }
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** "Mon DD, h:mm AM" for header metadata. */
export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * How far past its due date an item is, as a "by ..." suffix. Uses the
 * largest whole unit: minutes, then hours, then days.
 */
export function formatOverdueBy(iso: string, now: Date): string {
  const due = new Date(iso).getTime();
  if (Number.isNaN(due)) return "";
  const diffMs = now.getTime() - due;
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "by less than a minute";
  if (minutes < 60) return `by ${String(minutes)} ${minutes === 1 ? "minute" : "minutes"}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `by ${String(hours)} ${hours === 1 ? "hour" : "hours"}`;
  const days = Math.floor(hours / 24);
  return `by ${String(days)} ${days === 1 ? "day" : "days"}`;
}

/** Local wall-clock value for `<input type="datetime-local">`. */
export function toLocalDateTimeInput(date: Date): string {
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Matches `SnoozeWorkItemEndpoint.MaxHorizon` on the server. */
export const SNOOZE_MAX_HORIZON_DAYS = 30;

export const SNOOZE_QUICK_OPTIONS: readonly { readonly label: string; readonly minutes: number }[] =
  [
    { label: "15m", minutes: 15 },
    { label: "30m", minutes: 30 },
    { label: "1h", minutes: 60 },
    { label: "2h", minutes: 120 },
  ];

export function snoozeBounds(now: Date): { readonly min: string; readonly max: string } {
  const min = new Date(now.getTime() + 60_000);
  const max = new Date(now.getTime() + SNOOZE_MAX_HORIZON_DAYS * 24 * 60 * 60_000);
  return { min: toLocalDateTimeInput(min), max: toLocalDateTimeInput(max) };
}

/** A custom snooze target must be in the future and inside the horizon. */
export function isSnoozeTargetValid(value: string, nowMs: number): boolean {
  if (value.length === 0) return false;
  const target = new Date(value).getTime();
  if (Number.isNaN(target)) return false;
  if (target <= nowMs) return false;
  return target <= nowMs + SNOOZE_MAX_HORIZON_DAYS * 24 * 60 * 60_000;
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${String(count)} ${count === 1 ? singular : plural}`;
}
